import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  analyzeSessionHrData,
  getCachedSessionHrDataAnalysis,
  invalidateSessionHrDataAnalysisCache,
  repairSessionHrDataWithAi,
} from '../../../ai-repair';
import { getConfiguration } from '../../../../../services/configurationManager';
import {
  getEffectiveHeartRateData,
  parseHeartRateData,
} from '../../../shared/hr-data';
import type { HeartRateEvent } from '@model/DeviceConfiguration';
import type {
  DeterministicAnalysisResult,
  RepairPreview,
  SessionAnalysisState,
  SessionRepairLiteSession,
} from '../types';
import { buildRepairPreviewFromResult } from '../utils/buildRepairPreview';
import { deriveSessionRepairStatus } from '../utils/deriveSessionRepairStatus';
import {
  getAnalysisServiceErrorMessage,
  mapAnalysisResultToState,
} from '../utils/mapAnalysisResult';
import { runDeterministicAnalysis } from '../utils/runDeterministicAnalysis';

const buildInitialAnalysis = (
  deterministic: DeterministicAnalysisResult | null
): SessionAnalysisState => {
  if (!deterministic) {
    return { status: 'idle' };
  }

  return mapAnalysisResultToState({
    status: deterministic.status,
    findings: deterministic.findings,
    duplicateCount: deterministic.duplicateCount,
    gapCount: deterministic.gapCount,
    gapInsertionCount: deterministic.gapInsertionCount,
  });
};

export const useSessionRepairDetail = (sessionId: number) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sessionMeta, setSessionMeta] =
    useState<SessionRepairLiteSession | null>(null);
  const [originalEvents, setOriginalEvents] = useState<HeartRateEvent[]>([]);
  const [effectiveEvents, setEffectiveEvents] = useState<HeartRateEvent[]>([]);
  const [analysis, setAnalysis] = useState<SessionAnalysisState>({
    status: 'idle',
  });
  const [deterministicAnalysis, setDeterministicAnalysis] =
    useState<DeterministicAnalysisResult | null>(null);
  const [repairPreview, setRepairPreview] = useState<RepairPreview | null>(
    null
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await getConfiguration(sessionId);
      const data = response.data;
      const original = parseHeartRateData(data.hrData);
      const effective = getEffectiveHeartRateData(data);
      const deterministic =
        original.length > 0 ? runDeterministicAnalysis(original) : null;
      const cached = getCachedSessionHrDataAnalysis(sessionId);

      setSessionMeta({
        id: data.id,
        patientCode: data.patientCode ?? null,
        started_at: data.started_at ?? null,
        created_at: data.created_at,
        updated_at: data.updated_at ?? null,
        scenario: data.scenario ?? data.type ?? 'n/a',
        hasHrData: original.length > 0,
        hasHrDataRepaired: Boolean(data.hrData_repaired),
      });
      setOriginalEvents(original);
      setEffectiveEvents(effective);
      setDeterministicAnalysis(deterministic);
      setAnalysis(
        cached
          ? mapAnalysisResultToState(cached)
          : buildInitialAnalysis(deterministic)
      );
    } catch (error) {
      setLoadError(
        error instanceof Error
          ? error.message
          : 'Impossibile caricare la sessione.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const repairStatus = useMemo(
    () =>
      deriveSessionRepairStatus({
        hasHrData: sessionMeta?.hasHrData ?? false,
        hasHrDataRepaired: sessionMeta?.hasHrDataRepaired ?? false,
        analysis: deterministicAnalysis,
        isAnalyzing,
        analysisFailed: analysis.status === 'failed',
      }),
    [analysis.status, deterministicAnalysis, isAnalyzing, sessionMeta]
  );

  const runAnalyze = useCallback(async () => {
    setIsAnalyzing(true);
    try {
      const result = await analyzeSessionHrData(sessionId);
      setAnalysis(mapAnalysisResultToState(result));
      return result;
    } catch (error) {
      const message = getAnalysisServiceErrorMessage(
        error instanceof Error ? error : new Error('Analisi non riuscita.')
      );
      setAnalysis({ status: 'failed', message });
      throw new Error(message);
    } finally {
      setIsAnalyzing(false);
    }
  }, [sessionId]);

  const runRepair = useCallback(async () => {
    setIsRepairing(true);
    try {
      const result = await repairSessionHrDataWithAi(sessionId);
      const preview = buildRepairPreviewFromResult(result);

      if (preview) {
        setRepairPreview(preview);
        invalidateSessionHrDataAnalysisCache(sessionId);
        await reload();
      }

      return result;
    } finally {
      setIsRepairing(false);
    }
  }, [reload, sessionId]);

  return {
    isLoading,
    loadError,
    sessionMeta,
    originalEvents,
    effectiveEvents,
    analysis,
    deterministicAnalysis,
    repairStatus,
    repairPreview,
    isAnalyzing,
    isRepairing,
    runAnalyze,
    runRepair,
    reload,
  };
};
